import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { isProblemError } from '../../api/client';
import {
  useCreateParticipantGrade,
  useGetOccurrenceRecord,
  useListAssessmentTypes,
  useListGradeScales,
  usePutParticipantRecord,
} from '../../api/generated/academic/academic';
import { useGetOccurrence } from '../../api/generated/scheduling/scheduling';
import type {
  AssessmentType,
  ClassRecord,
  GradeScale,
  PutParticipantRecordRequest,
} from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
import { useGetMe } from '../../api/generated/identity/identity';
import { TextAreaField } from './TextAreaField';
import styles from './academic.module.css';

/** O que o contrato aceita em cada campo do registro (`PutParticipantRecordRequest`). */
const FIELD_MAX_LENGTH = 5000;
const JUSTIFICATION_MAX_LENGTH = 2000;

type EditableField = keyof Pick<
  PutParticipantRecordRequest,
  'contentTaught' | 'evolution' | 'recommendedExercises' | 'visibleComment' | 'internalComment'
>;

const FIELDS: EditableField[] = [
  'contentTaught',
  'evolution',
  'recommendedExercises',
  'visibleComment',
];

function draftFrom(record: ClassRecord | undefined): Record<EditableField, string> {
  return {
    contentTaught: record?.contentTaught ?? '',
    evolution: record?.evolution ?? '',
    recommendedExercises: record?.recommendedExercises ?? '',
    visibleComment: record?.visibleComment ?? '',
    internalComment: record?.internalComment ?? '',
  };
}

export function ClassRecordForm() {
  const { t } = useTranslation();
  const { occurrenceId = '' } = useParams();
  const meQuery = useGetMe();
  const me = meQuery.data;

  // A aula vem por id, e nao de uma varredura de doze meses de agenda.
  // `listOccurrences` recortava de seis meses atras a seis meses adiante: a aula
  // fora dessa faixa — e qualquer aula alem da primeira pagina, no dia em que a
  // operacao paginar — sumia do `find`, e a tela dizia "nenhum participante
  // nesta aula" sobre uma aula que existe. `getOccurrence` responde 404 quando a
  // aula nao existe, que e o estado que faltava a esta tela.
  const enabled = !!me && occurrenceId.length > 0;
  const occQuery = useGetOccurrence(occurrenceId, { query: { enabled } });
  const recordQuery = useGetOccurrenceRecord(occurrenceId, { query: { enabled } });

  if (!me) return null;

  const occurrence = occQuery.data;
  const participants = occurrence?.participants ?? [];
  const records = recordQuery.data?.records ?? [];
  // O que este campo responde e "ja houve uma edicao apos a janela", e nao "a
  // janela fechou": quem decide a segunda e o relogio do servidor contra o fim
  // da ocorrencia mais a janela (`api/internal/academic/record.go:85`), e o
  // contrato ainda nao expoe esse instante. Enquanto nao expuser, o unico jeito
  // honesto de saber e perguntar — e quem pergunta e o proprio PUT, cujo 422
  // abre o campo de justificativa no painel.
  const afterWindow = records.some((record) => record.editedAfterWindow);

  // Sem isto o painel monta antes de o registro chegar e semeia o rascunho
  // vazio: o professor abre uma aula ja lancada, ve campos em branco, corrige
  // uma linha e salva — e o PUT, que substitui os cinco campos de uma vez,
  // apaga o que estava gravado. Sao duas requisicoes independentes, e a da
  // agenda costuma responder primeiro.
  const loading = occQuery.isPending || recordQuery.isPending;
  const failed = occQuery.isError || recordQuery.isError;

  return (
    <div className={styles.page}>
      <h1>{t('academic.record.title')}</h1>
      {occurrence ? (
        <p className={styles.context}>
          {formatDateInPersonTimezone(occurrence.startsAtUtc, me.timezone, me.locale)}
        </p>
      ) : null}
      {failed ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {loading && !failed ? (
        <p className={styles.meta} role="status">
          {t('app.loading')}
        </p>
      ) : null}
      {afterWindow ? (
        <Alert tone="warning" title={t('academic.record.afterWindowTitle')}>
          {t('academic.record.afterWindowBody')}
        </Alert>
      ) : null}
      {participants.length === 0 && !loading && !failed ? (
        <p>{t('academic.record.empty')}</p>
      ) : null}
      {loading || failed ? null : (
        <div className={styles.participants}>
          {participants.map((participant) => (
            <ParticipantPanel
              key={participant.id}
              occurrenceId={occurrenceId}
              participantId={participant.id}
              label={participant.student.displayName}
              record={records.find((item) => item.participantId === participant.id)}
              afterWindow={afterWindow}
              onSaved={() => void recordQuery.refetch()}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ParticipantPanel({
  occurrenceId,
  participantId,
  label,
  record,
  afterWindow,
  onSaved,
}: {
  readonly occurrenceId: string;
  readonly participantId: string;
  readonly label: string;
  readonly record: ClassRecord | undefined;
  readonly afterWindow: boolean;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation();
  const save = usePutParticipantRecord();
  const [draft, setDraft] = useState(() => draftFrom(record));
  const [justification, setJustification] = useState('');
  // Sticky: uma vez que o servidor disse que a janela fechou, o campo nao pode
  // sumir de novo enquanto a tela estiver aberta.
  const [windowClosed, setWindowClosed] = useState(false);
  const [refusal, setRefusal] = useState<'justification' | 'other' | null>(null);

  // Re-semeadura quando o registro carregado muda de identidade — depois de
  // salvar, o painel passa a mostrar o que o servidor guardou, e nao o rascunho
  // que ficou na tela. Ajustar estado durante a renderizacao e o padrao do React
  // para isto; a alternativa (`key` com `updatedAt`) remontaria o painel inteiro
  // e derrubaria junto o lancamento de nota que estava pela metade ao lado.
  const [seededFrom, setSeededFrom] = useState(record?.updatedAt ?? null);
  if ((record?.updatedAt ?? null) !== seededFrom) {
    setSeededFrom(record?.updatedAt ?? null);
    setDraft(draftFrom(record));
  }

  const needsJustification = afterWindow || windowClosed;
  const canSave = !needsJustification || justification.trim().length > 0;

  const setField = (field: EditableField) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const submit = (): void => {
    const filled = justification.trim();
    setRefusal(null);
    save.mutate(
      {
        occurrenceId,
        participantId,
        data: {
          contentTaught: draft.contentTaught || null,
          evolution: draft.evolution || null,
          recommendedExercises: draft.recommendedExercises || null,
          visibleComment: draft.visibleComment || null,
          internalComment: draft.internalComment || null,
          // A justificativa era coletada e descartada: o PUT saia sem ela e o
          // servidor recusava toda edicao tardia com 422. Dentro da janela o
          // servidor a ignora, entao mandar sempre que preenchida nao muda nada
          // no caminho comum e conserta o tardio.
          ...(filled.length > 0 ? { justification: filled } : {}),
        },
      },
      {
        onSuccess: onSaved,
        onError: (error) => {
          // 422 depois de um envio SEM justificativa e a resposta do servidor
          // sobre a janela — a unica fonte que sabe se ela fechou. Com
          // justificativa preenchida, um 422 fala de outra coisa e nao pode
          // virar "informe a justificativa", que ja esta informada.
          const closed = filled.length === 0 && isProblemError(error) && error.status === 422;
          setRefusal(closed ? 'justification' : 'other');
          if (closed) setWindowClosed(true);
        },
      },
    );
  };

  return (
    <section className={styles.participant} aria-label={label}>
      <h2 className={styles.participantName}>{label}</h2>

      {FIELDS.map((field) => (
        <TextAreaField
          key={field}
          label={t(`academic.record.field.${field}`)}
          value={draft[field]}
          maxLength={FIELD_MAX_LENGTH}
          onChange={setField(field)}
        />
      ))}

      <div className={styles.internalBlock}>
        <TextAreaField
          label={t('academic.record.field.internalComment')}
          hint={t('academic.record.internalHint')}
          value={draft.internalComment}
          maxLength={FIELD_MAX_LENGTH}
          onChange={setField('internalComment')}
        />
      </div>

      {needsJustification ? (
        <TextAreaField
          label={t('academic.record.justification')}
          hint={t('academic.record.justificationHint')}
          required
          rows={2}
          maxLength={JUSTIFICATION_MAX_LENGTH}
          value={justification}
          onChange={setJustification}
        />
      ) : null}

      {/*
        A recusa era engolida inteira: o botao parava de girar, o texto
        continuava na tela e o professor ia embora acreditando ter gravado.
      */}
      {refusal === 'justification' ? (
        <Alert tone="warning" title={t('academic.record.afterWindowTitle')}>
          {t('academic.record.afterWindowBody')}
        </Alert>
      ) : null}
      {refusal === 'other' ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <Button variant="primary" loading={save.isPending} disabled={!canSave} onClick={submit}>
        {t('academic.record.save')}
      </Button>

      <GradeEntry occurrenceId={occurrenceId} participantId={participantId} />
    </section>
  );
}

/**
 * Lancamento de nota, junto do registro da aula.
 *
 * O professor registrava a aula e **nao lancava nota**: `createParticipantGrade`
 * nao tinha tela nenhuma. Fica aqui, e nao numa pagina separada, porque quem
 * acabou de escrever a evolucao do aluno e quem tem a nota na cabeca.
 *
 * **Exatamente um valor vai no corpo**, e qual deles depende do `kind` da
 * escala do tipo escolhido: numerica, conceito ou rubrica. Mandar dois — ou
 * nenhum — responde 422, e por isso o formulario troca o campo em vez de
 * mostrar os tres e confiar em quem preenche.
 */
function GradeEntry({
  occurrenceId,
  participantId,
}: {
  readonly occurrenceId: string;
  readonly participantId: string;
}) {
  const { t } = useTranslation();
  const typesQuery = useListAssessmentTypes();
  const scalesQuery = useListGradeScales();
  const create = useCreateParticipantGrade();

  const [assessmentTypeId, setAssessmentTypeId] = useState('');
  const [value, setValue] = useState('');

  // Tipo inativo nao recebe nota nova: ele existe para sustentar a nota antiga,
  // cujo peso ficou congelado no lancamento.
  const types: readonly AssessmentType[] = (typesQuery.data ?? []).filter((type) => type.active);
  const scales: readonly GradeScale[] = scalesQuery.data ?? [];

  const type = types.find((candidate) => candidate.id === assessmentTypeId);
  const scale = scales.find((candidate) => candidate.id === type?.scaleId);

  const numeric = scale?.kind === 'numeric';
  const parsed = Number(value);
  const numericValid =
    Number.isFinite(parsed) &&
    (scale?.minValue === null || scale?.minValue === undefined || parsed >= scale.minValue) &&
    (scale?.maxValue === null || scale?.maxValue === undefined || parsed <= scale.maxValue);

  const canSubmit =
    type !== undefined &&
    scale !== undefined &&
    value.length > 0 &&
    (numeric ? numericValid : true) &&
    !create.isPending;

  const submit = (): void => {
    if (!type || !scale) return;
    const body =
      scale.kind === 'numeric'
        ? { assessmentTypeId: type.id, numericValue: parsed }
        : scale.kind === 'concept'
          ? { assessmentTypeId: type.id, conceptValue: value }
          : { assessmentTypeId: type.id, rubricValue: value };

    create.mutate(
      { occurrenceId, participantId, data: body },
      { onSuccess: () => setValue('') },
    );
  };

  return (
    <div className={styles.grades}>
      <h3 className={styles.subheading}>{t('academic.record.grade.title')}</h3>

      {create.isSuccess ? (
        <Alert tone="success">{t('academic.record.grade.recorded')}</Alert>
      ) : null}
      {create.isError ? <Alert tone="error">{t('academic.record.grade.refused')}</Alert> : null}

      <label className={styles.selectLabel}>
        {t('academic.record.grade.type')}
        <select
          value={assessmentTypeId}
          onChange={(event) => {
            setAssessmentTypeId(event.target.value);
            setValue('');
          }}
        >
          <option value="">{t('academic.record.grade.typePlaceholder')}</option>
          {types.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {scale === undefined ? null : numeric ? (
        <Field
          label={t('academic.record.grade.numericValue')}
          type="number"
          required
          value={value}
          onChange={(event) => setValue(event.target.value)}
          hint={
            scale.minValue !== null && scale.maxValue !== null
              ? t('academic.record.grade.range', { min: scale.minValue, max: scale.maxValue })
              : undefined
          }
          error={value.length > 0 && !numericValid ? t('academic.record.grade.outOfRange') : undefined}
        />
      ) : (
        <label className={styles.selectLabel}>
          {t('academic.record.grade.allowedValue')}
          <select value={value} onChange={(event) => setValue(event.target.value)}>
            <option value="">{t('academic.record.grade.valuePlaceholder')}</option>
            {scale.allowedValues.map((allowed) => (
              <option key={allowed} value={allowed}>
                {allowed}
              </option>
            ))}
          </select>
        </label>
      )}

      <div>
        <Button variant="secondary" disabled={!canSubmit} loading={create.isPending} onClick={submit}>
          {t('academic.record.grade.submit')}
        </Button>
      </div>
    </div>
  );
}
